import React, { ReactNode } from 'react';

import { connect } from 'react-redux';

import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import { CommentAction as AkCommentAction } from '@atlaskit/comment';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';

import { FabricUser } from 'src/components/bbc-atlaskit-editor';
import { FabricComment } from 'src/components/conversation-provider/types';
import { toConversationId } from 'src/components/conversation-provider/utils/to-conversation';
import { ConversationsContextInterface } from 'src/contexts/conversations-context';
import { HIGHLIGHT_COMMENT } from 'src/redux/pull-request/actions';
import {
  getCurrentPullRequest,
  getCurrentPullRequestId,
  getCurrentPullRequestRoleForCurrentUser,
} from 'src/redux/pull-request/selectors';
import { getCurrentFabricUser } from 'src/selectors/conversation-selectors';
import { getCurrentRepositoryUuid } from 'src/selectors/repository-selectors';
import { BucketDispatch } from 'src/types/state';

import { ResourceProvider } from '../api/ConversationResource';
import { CommentThread } from '../components/comment-tree';
import {
  Conversation,
  Props as ConversationComponentProps,
} from '../components/Conversation';
import { ConversationResolution, SuccessHandler } from '../components/types';
import { uuid } from '../internal/uuid';

// Returned by mapDispatchToProps
type DispatchProps = Pick<
  ConversationComponentProps,
  | 'onCreateConversation'
  | 'onAddComment'
  | 'onUpdateComment'
  | 'onResolveComment'
  | 'onReopenComment'
  | 'onDeleteComment'
  | 'onHighlightComment'
>;

const mapDispatchToProps = (
  dispatch: any,
  { provider, conversationsContext, commentThreads }: ResourcedConversationProps
): DispatchProps => ({
  onAddComment(
    conversationId: string,
    parentId: number | null,
    value: any,
    pending?: boolean,
    onSuccess?: SuccessHandler
  ) {
    dispatch(async (bucketDispatch: BucketDispatch, storeState: () => any) => {
      const state = storeState();
      const commentThread = commentThreads.find(thread => {
        return toConversationId(thread.topComment) === conversationId;
      });
      if (!commentThread) {
        // TODO remove this as soon as we move to a single commentThread model
        // This is primarily to keep the typing happy
        // See https://softwareteams.atlassian.net/browse/COREX-7955 for some of this work
        return;
      }
      const user = getCurrentFabricUser(state) || undefined;
      const { commentId } = await provider.addComment(
        bucketDispatch,
        user,
        commentThread,
        conversationId,
        parentId,
        value,
        pending,
        conversationsContext.baseRevHash
      );

      if (typeof onSuccess === 'function') {
        onSuccess(commentId.toString());
      }

      conversationsContext.eventHandlers?.onCommentCreate({
        commentId: commentId.toString(),
        role: getCurrentPullRequestRoleForCurrentUser(state),
        pullRequestLocalId: getCurrentPullRequestId(state),
        repoUuid: getCurrentRepositoryUuid(state),
        pullRequestStatus: getCurrentPullRequest(state)?.state,
        pending,
      });
    });
  },

  onUpdateComment(commentId: number, value: any, onSuccess?: SuccessHandler) {
    dispatch(async (bucketDispatch: BucketDispatch) => {
      await provider.updateComment(bucketDispatch, commentId, value);

      if (typeof onSuccess === 'function') {
        onSuccess(commentId.toString());
      }
    });
  },

  onResolveComment(
    commentId: number,
    conversationResolution: ConversationResolution,
    onSuccess?: SuccessHandler
  ) {
    dispatch(async (bucketDispatch: BucketDispatch) => {
      await provider.resolveComment(
        bucketDispatch,
        commentId,
        conversationResolution
      );

      if (typeof onSuccess === 'function') {
        onSuccess(commentId.toString());
      }
    });
  },

  onReopenComment(
    commentId: number,
    conversationResolution: ConversationResolution,
    onSuccess?: SuccessHandler
  ) {
    dispatch(async (bucketDispatch: BucketDispatch) => {
      await provider.reopenComment(
        bucketDispatch,
        commentId,
        conversationResolution
      );

      if (typeof onSuccess === 'function') {
        onSuccess(commentId.toString());
      }
    });
  },

  onDeleteComment(commentId: number, onSuccess?: SuccessHandler) {
    dispatch(async (bucketDispatch: BucketDispatch) => {
      await provider.deleteComment(bucketDispatch, commentId);

      if (typeof onSuccess === 'function') {
        onSuccess(commentId.toString());
      }
    });
  },

  onHighlightComment(
    _event: React.MouseEvent<HTMLAnchorElement>,
    commentId: number
  ) {
    dispatch({
      type: HIGHLIGHT_COMMENT,
      payload: commentId,
    });
  },

  onCreateConversation(
    localId: string,
    value: any,
    meta: any,
    _objectId: string,
    containerId?: string,
    pending?: boolean,
    onSuccess?: SuccessHandler
  ) {
    dispatch(async (bucketDispatch: BucketDispatch, storeState: () => any) => {
      const state = storeState();
      const user = getCurrentFabricUser(state) || undefined;
      const { conversationId } = await provider.create(
        bucketDispatch,
        user,
        localId,
        value,
        meta,
        containerId,
        pending,
        conversationsContext.baseRevHash
      );

      if (typeof onSuccess === 'function') {
        onSuccess(conversationId);
      }
    });
  },
});

type ResourcedConversationProps = ContainerProps & { localId: string };

const ResourcedConversation = withAnalyticsEvents()(
  connect<{}, DispatchProps, ResourcedConversationProps>(
    undefined,
    mapDispatchToProps
  )(Conversation)
);

export interface ContainerProps {
  id?: string;
  localId?: string;
  objectId: string;
  canModerateComments?: boolean;
  containerId?: string;
  provider: ResourceProvider;
  dataProviders?: ProviderFactory;
  meta?: {
    [key: string]: any;
  };
  isExpanded?: boolean;
  onCancel?: () => void;
  onCommentPermalinkClick?: (
    event: React.MouseEvent<HTMLAnchorElement>,
    commentId: number
  ) => void;
  showBeforeUnloadWarning?: boolean;
  showDeleteCommentDialog?: boolean;
  onEditorOpen?: () => void;
  onEditorClose?: () => void;
  onEditorChange?: () => void;
  placeholder?: string;
  disableScrollTo?: boolean;
  allowHelpButton?: boolean;
  portal?: HTMLElement;
  renderAdditionalCommentActions?: (
    CommentAction: typeof AkCommentAction,
    comment: FabricComment
  ) => JSX.Element[];
  renderAfterComment?: (comment: FabricComment) => JSX.Element;
  renderAuthorAvatar?: (user?: FabricUser) => ReactNode;
  renderAuthorName?: (user?: FabricUser) => ReactNode;
  maxCommentNesting?: number;
  loginLink?: string;
  loginLinkText?: string;
  canCollapseThread?: boolean;
  canResolveThread?: boolean;
  conversationsContext: ConversationsContextInterface;
  renderCommentWithReplyMention?: boolean;
  popupsMountPoint?: HTMLElement | null;
  commentThreads: CommentThread[];
  codeSuggestionsFF?: boolean;
}

type ContainerState = {
  localId: string;
};

class ConversationContainer extends React.Component<
  ContainerProps,
  ContainerState
> {
  constructor(props: ContainerProps) {
    super(props);
    this.state = {
      localId: props.id || (uuid.generate() as string),
    };
  }

  render() {
    const {
      props,
      state: { localId },
    } = this;
    return (
      <ResourcedConversation
        {...props}
        conversationId={props.id || localId}
        localId={localId}
      />
    );
  }
}

export default ConversationContainer;
